import { useMemo, useState } from "react";
import { AccionesUsuarioCabecera, FilaVerComoMovil } from "@/components/admin/AccionesUsuarioCabecera";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Package, Inbox, BarChart3, Handshake, CreditCard, FileText, Ticket, Megaphone, ClipboardCheck, Headset,
  Menu, type LucideIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { TabBarPlano } from "@/components/ui/tab-bar";
import { KpiCardUniforme } from "@/components/ui/kpi-card";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { usePortalNavEstado } from "@/hooks/usePortalNav";
import { supabase } from "@/integrations/supabase/client";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { usePublicarVerComoCobranza } from "@/contexts/CobranzaImpersonationContext";
import { useCobranzaEffectiveIdentity } from "@/hooks/usePortalPagePermissions";
import { resolvePortalNavRole } from "@/lib/impersonation/page-permissions";
import { CobranzaImpersonationSelector } from "@/components/admin/portal-cobranza/CobranzaImpersonationSelector";
import { usePerfilVista } from "@/hooks/usePerfilVista";

/**
 * Layout del Portal Productos, con el estándar del Portal Cobranza: sidebar con grupos, header
 * con el nombre de la sección y el perfil, y pestañas planas (solo texto) en todo el portal.
 *
 * "Ver como usuario..." es el mismo del Portal Cobranza (comparten contexto). Su valor está en la
 * "Vista del usuario": menús y botones con el rol del impersonado, edición bloqueada. Los DATOS
 * siguen viniendo con la sesión real: la RPC del portal filtra por el token del admin, así que un
 * recorte que solo viva en RLS/RPC no se simula (ver `lib/impersonation/page-permissions`).
 */

interface NavLeaf { label: string; path: string; icon: LucideIcon }
interface NavGroup { label: string; items: NavLeaf[] }

const BASE = "/admin/portal-productos";

/**
 * Menú "Portal Productos" en `menus`. Se resuelve por id (igual en dev y prod), nunca por
 * nombre: renombrar el menú o sus submenús en el admin no rompe el portal. El nombre del
 * portal y de cada submenú se leen de la BD.
 */
const PORTAL_PRODUCTOS_MENU_ID = 36;

/**
 * El ícono y el grupo no viven en BD: se resuelven por ruta. Mismo esquema que el Portal
 * Cobranza (del que este portal es espejo acotado a productos): todo en "Operación" y Reportes
 * en "Herramientas".
 */
const ICONOS: Record<string, LucideIcon> = {
  [`${BASE}/productos`]: Package,
  [`${BASE}/cuentas-cobranza`]: Inbox,
  [`${BASE}/relacion-pagos`]: CreditCard,
  [`${BASE}/atencion-clientes`]: Headset,
  [`${BASE}/promesas`]: Handshake,
  [`${BASE}/reportes`]: BarChart3,
  [`${BASE}/tickets`]: Ticket,
};
const GRUPO_DE_RUTA: Record<string, string> = {
  [`${BASE}/reportes`]: "Herramientas",
};
const ORDEN_GRUPOS = ["Operación", "Aprobaciones", "Comunicación", "Herramientas"];

const RUTA_AVISOS = `${BASE}/comunicacion/avisos`;
GRUPO_DE_RUTA[RUTA_AVISOS] = "Comunicación";
ICONOS[RUTA_AVISOS] = Megaphone;

/** Ofertas de producto (submenú "Ofertas"): grupo "Aprobaciones", después de Operación. */
const RUTA_APROBACION_OFERTAS = `${BASE}/aprobacion-ofertas`;
GRUPO_DE_RUTA[RUTA_APROBACION_OFERTAS] = "Aprobaciones";
ICONOS[RUTA_APROBACION_OFERTAS] = ClipboardCheck;

/** Nombre del menú por id; mientras carga o si falla se usa el nombre oficial. */
function useNombrePortal(): string {
  const { data } = useQuery({
    queryKey: ["menu-nombre", PORTAL_PRODUCTOS_MENU_ID],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("menus")
        .select("nombre")
        .eq("id", PORTAL_PRODUCTOS_MENU_ID)
        .maybeSingle();
      if (error) return null;
      return (data?.nombre as string | undefined) ?? null;
    },
    staleTime: 5 * 60_000,
  });
  return data ?? "Portal Productos";
}

/** Rutas de detalle que no están en el menú. */
const DETALLE_CUENTA = /^\/admin\/portal-productos\/cuentas-cobranza\/\d+\/detalle/;

export const PortalProductosLayout = () => {
  // Solo mientras este layout está montado se ve como el usuario (y en solo lectura).
  usePublicarVerComoCobranza();
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const { isPathDisabled, isPathAllowed } = useAllowedMenus();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nombrePortal = useNombrePortal();
  const { items: nav, cargando: navCargando } = usePortalNavEstado(PORTAL_PRODUCTOS_MENU_ID, ICONOS, Package);
  const canImpersonate = profile?.puede_impersonar === true;

  // "Vista del usuario": el menú se pinta con los submenús en los que el rol del impersonado
  // tiene algún permiso activo (mismo criterio que el Portal Cobranza). Sin impersonar,
  // solo lo que el rol de la sesión puede abrir (isPathAllowed): antes se
  // pintaban todos los submenús activos, el mismo defecto que tuvo Escrituración.
  const navRole = resolvePortalNavRole(useCobranzaEffectiveIdentity());
  const filtraPorRol = navRole.simulated && !navRole.showAll && navRole.rolId != null;
  const { data: rutasDelRol } = useQuery({
    queryKey: ["portal-productos-nav-rol", navRole.rolId],
    enabled: filtraPorRol,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: subs } = await (supabase as any)
        .from("submenus")
        .select("id, vista_front_end")
        .eq("menu_id", PORTAL_PRODUCTOS_MENU_ID)
        .eq("activo", true);
      if (!subs?.length) return [] as string[];
      const { data: perms } = await (supabase as any)
        .from("submenus_permisos")
        .select("submenu_id")
        .in("submenu_id", (subs as any[]).map((s) => s.id))
        .eq("rol_id", navRole.rolId)
        .eq("activo", true);
      const ids = new Set(((perms as any[]) ?? []).map((p) => p.submenu_id));
      return (subs as any[]).filter((s) => ids.has(s.id)).map((s) => s.vista_front_end as string);
    },
  });

  // Submenús del menú por id, agrupados por ruta; se ocultan los apagados en BD.
  const navGroups = useMemo<NavGroup[]>(() => {
    const delRol = filtraPorRol ? new Set(rutasDelRol ?? []) : null;
    const visibles = nav.filter((it) =>
      !isPathDisabled(it.path) && (delRol ? delRol.has(it.path) : isPathAllowed(it.path)));
    return ORDEN_GRUPOS
      .map((label) => ({ label, items: visibles.filter((it) => (GRUPO_DE_RUTA[it.path] ?? "Operación") === label) }))
      .filter((g) => g.items.length > 0);
  }, [nav, isPathDisabled, isPathAllowed, filtraPorRol, rutasDelRol]);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");
  const currentSection = DETALLE_CUENTA.test(location.pathname)
    ? "Cuenta de producto"
    : nav.find((it) => isActive(it.path))?.label ?? nombrePortal;

  const handleNavigate = (path: string) => { navigate(path); setMobileOpen(false); };

  // Perfil: con "Ver como", la persona impersonada (nombre y rol suyos), no el admin.
  const perfilVista = usePerfilVista({ rol: profile?.rol_nombre ?? "Productos" });
  const userName = perfilVista.nombre.split(/\s+/).slice(0, 2).join(" ");
  const userRole = perfilVista.rol || "Productos";
  const initials = perfilVista.iniciales;

  const sidebarContent = (
    <PortalSidebar
      portal={nombrePortal}
      miCuenta
      cargando={navCargando}
      groups={navGroups.map((group) => ({
        label: group.label,
        items: group.items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }))}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => handleNavigate("/admin") : undefined}
      onCerrarSesion={signOut}
    />
  );

  return (
    <div className="min-h-screen flex antialiased">
      <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
        {sidebarContent}
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
          {sidebarContent}
        </SheetContent>
      </Sheet>

      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="hidden lg:flex items-center justify-between gap-3 sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border-soft px-6 h-14 shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]">
          <h1 className="min-w-0 flex-1 truncate text-xl font-bold text-foreground tracking-tight">{currentSection}</h1>
          <AccionesUsuarioCabecera verComo={canImpersonate ? <CobranzaImpersonationSelector menuId={PORTAL_PRODUCTOS_MENU_ID} /> : undefined} />
        </header>

        <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card/95 backdrop-blur-sm border-b border-border shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between px-3 h-14">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-1.5 -ml-1 rounded-lg text-foreground hover:bg-muted transition-colors"
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" />
            </button>
            <p className="text-[16px] font-bold text-foreground tracking-tight truncate">{currentSection}</p>
          </div>
          <AccionesUsuarioCabecera compacto />
          </div>
          {/* "Ver como" en móvil: segunda fila, igual que el Portal Agente. */}
          {canImpersonate && (
            <FilaVerComoMovil>
              <CobranzaImpersonationSelector menuId={PORTAL_PRODUCTOS_MENU_ID} />
            </FilaVerComoMovil>
          )}
        </header>


        <main className="min-w-0 overflow-x-clip px-4 py-5 sm:px-8 bg-background min-h-[calc(100vh-56px)]">
          {/* Pestañas de todo el portal: solo texto, sin íconos ni tooltips. Tarjetas KPI de todo
              el portal: un solo tamaño, igual que en Cobranza (las pantallas se reutilizan). */}
          <TabBarPlano>
            <KpiCardUniforme>
              <Outlet />
            </KpiCardUniforme>
          </TabBarPlano>
        </main>
      </div>
    </div>
  );
};

export default PortalProductosLayout;
